<template>
  <div class="container-fluid">
    <div class="row">
      <b-modal :modal-class="{ 'time-off-modal': true, 'loading': isSubmittingModal }" size="md" hide-footer
        ref="TimeOffsSettingsModal" centered>
        <template v-slot:modal-header="{ cancel }">
          <h5 class="modal-title">
            {{
              !!editedItem.id
                ? "Edit time offs settings"
                : "Create time offs settings"
            }}
          </h5>
          <button type="button" aria-label="Close" class="close" @click.prevent="cancel">
            <span class="material-icons"> close </span>
          </button>
        </template>
        <TimeOffsSettingsForm :dataProps="editedItem" :isLoading="isLoading" @submit="onSubmit" />
      </b-modal>

      <div class="col-12 full-height-page-wrapper">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs page-title="Time offs settings">
            <template #buttons>
              <b-button variant="success" class="button" @click="editEntityModalOpen()" :disabled="isLoading">
                Create
              </b-button>
            </template>
          </PageHeader>
        </div>

        <div v-if="isLoading" class="content-loading-wrapper" style="min-height: 60vh">
          <Preloader class="centered-preloader large-spinner" />
        </div>

        <TableForPage v-else-if="timeOffsSettings.length" :tableData="timeOffsSettings" :fields="table.fields"
          :slots="table.slots" @edit="editEntityModalOpen" @remove="deleteTimeOffsSetting">
          <template v-slot:icon="slotProps">
            <TimeOffIcon :name="slotProps.item.icon" variant="secondary" />
          </template>
          <template v-slot:is_deactivated="slotProps">
            <TimeOffSettingStatus :timeSetting="slotProps.item" />
          </template>
          <template v-slot:color="slotProps">
            <div :style="{ backgroundColor: slotProps.item.color, borderRadius: '3px' }"
              style="width: 20px; height: 20px"></div>
          </template>
          <template v-slot:is_payable="slotProps">
            <div v-if="slotProps.item.is_payable">
              <font-awesome-icon class="checkbox-icon" icon="check-square" size="1x" />
            </div>
          </template>
          <template v-slot:is_unlimited="slotProps">
            <div v-if="slotProps.item.is_unlimited">
              <font-awesome-icon class="checkbox-icon" icon="check-square" size="1x" />
            </div>
          </template>
          <template v-slot:approver_roles="slotProps">
            <div v-for="approverRole in slotProps.item.approver_roles" :key="approverRole.id">
              {{ approverRole.display_name }}
            </div>
          </template>
          <template v-slot:action="slotProps">
            <div class="d-flex align-items-center gap-1" role="group">
              <span class="icon-size cursor-pointer" @click="editEntityModalOpen(slotProps.item)">
                <span v-html="editIconSvg"></span>
              </span>

              <span v-if="slotProps.item.is_deletable" class="icon-size cursor-pointer"
                @click="deleteTimeOffsSetting(slotProps.item)">
                <span v-html="removeIconSvg"></span>
              </span>

              <template v-else>
                <b-tooltip :target="`tooltip-target-${slotProps.index}`" custom-class="time-offs-settings-delete"
                  variant="base" container="body" boundary="viewport" triggers="hover" placement="right">
                  <span>This time off type can’t be deleted if historical records exist — deactivate it instead. If
                    there are future time offs or requests, delete or edit them first.</span>
                </b-tooltip>
                <span class="icon-size cursor-disabled" :id="`tooltip-target-${slotProps.index}`">
                  <span v-html="disabledRemoveIconSvg"></span>
                </span>
              </template>

            </div>
          </template>
        </TableForPage>

        <div v-else class="full-height-content">
          <EmptyStateNoResults customIcon="calendar_clock"
            customIconClasses="material-symbols-outlined empty-state__icon" title="No time off settings configured"
            description="Set up time off policies to manage leave types, accruals, and approval workflows for your team."
            buttonText="CREATE" @button-click="handleCreateClick" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TimeOffsSettingsForm from '@components/forms/TimeOffsSettingsForm.vue'
import TableForPage from '@tables/TableForPage'
import TimeOffSettingStatus from '@components/TimeOffSettingStatus'
import TimeOffsSettings from '../mixins/TimeOffsSettings'
import { mapGetters } from 'vuex'
import TimeOffIcon from '@components/TimeOffIcon.vue'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'
import removeIcon from '@/assets/icons/removeIcon'
import editIcon from '@/assets/icons/editIcon'

export default {
  name: 'TimeOffsSettingsPage',
  components: {
    TableForPage,
    TimeOffsSettingsForm,
    TimeOffIcon,
    PageHeader,
    Preloader,
    EmptyStateNoResults,
    TimeOffSettingStatus
  },
  mixins: [TimeOffsSettings],
  data () {
    return {
      removeIconSvg: removeIcon(),
      disabledRemoveIconSvg: removeIcon({ color: '#CACEDB' }),
      editIconSvg: editIcon(),
      isLoading: false,
      isSubmittingModal: false,
      isOpenDialog: false,
      editedItem: {},
      table: {
        data: [],
        fields: [
          {
            key: 'icon',
            type: 'slot',
            slotName: 'icon',
            label: 'Icon',
            class: 'column-icon'
          },
          {
            key: 'name',
            label: 'Name',
            sortable: true
          },
          {
            key: 'is_deactivated',
            type: 'slot',
            slotName: 'is_deactivated',
            label: 'Status',
            sortable: true
          },
          {
            key: 'color',
            type: 'slot',
            slotName: 'color',
            label: 'Color'
          },
          {
            key: 'is_payable',
            type: 'slot',
            slotName: 'is_payable',
            label: 'Payable'
          },
          {
            key: 'is_unlimited',
            type: 'slot',
            slotName: 'is_unlimited',
            label: 'Unlimited days per year'
          },
          {
            key: 'approver_roles',
            type: 'slot',
            slotName: 'approver_roles',
            label: 'Approver roles'
          },
          {
            key: 'action',
            type: 'slot',
            label: 'Actions',
            class: 'column-action-timeoff'
          }
        ],
        slots: {
          icon: { name: 'icon' },
          color: { name: 'color' },
          is_deactivated: { name: 'is_deactivated' },
          is_payable: { name: 'is_payable' },
          is_unlimited: { name: 'is_unlimited' },
          approver_roles: { name: 'approver_roles' },
          action: { name: 'action' }
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      timeOffsSettings: 'timeOffsSettings/getTimeOffsSettings'
    })
  },

  async mounted () {
    await this.fetchTimeOffsSettings()
  },

  methods: {
    async fetchTimeOffsSettings () {
      this.isLoading = true
      await this._toggleDataPageLoading(() =>
        this.$api.timeOffsSettings.getTimeOffsSettingsFull()
      )
      this.isLoading = false
    },
    editEntityModalOpen (item) {
      if (item) {
        this.editedItem = item
      } else {
        this.editedItem = {}
      }

      this.isOpenDialog = true
      this.isSubmittingModal = false
      this.$refs['TimeOffsSettingsModal'].show()
    },

    async onSubmit (data) {
      if (this.editedItem.id) {
        await this.updateTimeOffsSetting({ ...data, id: this.editedItem.id })
      } else {
        await this.createTimeOffsSetting(data)
      }
      this.fetchTimeOffsSettings()
    },

    async createTimeOffsSetting (data) {
      this.isSubmittingModal = true

      return this.$api.timeOffsSettings
        .createTimeOffsSetting(data)
        .then(({ data }) => {
          this.$store.dispatch('timeOffsSettings/addTimeOffsSetting', data)

          this.$snotify.success(
            'Time offs setting successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.isOpenDialog = false
          this.editedItem = {}
          this.$refs['TimeOffsSettingsModal'].hide()
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isSubmittingModal = false
        })
    },
    async updateTimeOffsSetting (data) {
      this.isSubmittingModal = true
      return this.$api.timeOffsSettings
        .editTimeOffsSetting(data)
        .then(({ data }) => {
          this.$store.dispatch('timeOffsSettings/editTimeOffsSetting', data)

          this.$snotify.success(
            'Time offs setting successfully updated',
            'Success',
            this.$store.state.snotify
          )

          this.isOpenDialog = false
          this.editedItem = {}
          this.$refs['TimeOffsSettingsModal'].hide()
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isSubmittingModal = false
        })
    },
    async deleteTimeOffsSetting (item) {
      if (!item.is_deletable) {
        return
      }

      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete?'
      )

      if (!confirm) {
        return
      }
      this.isLoading = true
      this.$api.timeOffsSettings
        .removeTimeOffsSetting(item.id)
        .then(({ data }) => {
          this.$store.dispatch(
            'timeOffsSettings/removeTimeOffsSetting',
            parseInt(data.id)
          )
          this.$snotify.success(
            'Time offs setting successfully deleted',
            'Success',
            this.$store.state.snotify
          )

          this.editedItem = {}
          this.fetchTimeOffsSettings()
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleCreateClick () {
      this.editEntityModalOpen()
    }
  }
}
</script>

<style lang="scss" scoped>
.checkbox-icon {
  color: $fontColorAccent;
}

.icon-size {
  width: 20px;
  height: 20px;
}

</style>
